import { useCallback, useRef } from 'react';

/**
 * Creates a debounced version of a callback, which will only be called after a delay if no other call has been made to the debounced function.
 *
 * @param callback The callback to call after the delay (if no other call has been made)
 * @param delay The delay in milliseconds to wait before calling the callback
 * @returns The debounced callback
 */
export const useDebouncedCallback = <TFunction extends (...args: any[]) => void>(callback: TFunction, delay = 500) => {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>();

  return useCallback(
    (...args: unknown[]) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => callback(...args), delay);
    },
    [callback, delay]
  ) as TFunction;
};
